/* istanbul ignore file */
// Sentry SDK initialization wrapper — not unit-testable without mocking
// the entire SDK. Behavior verified by manual smoke test on real envs
// (events appear / don't appear in the Sentry dashboard per expected
// gating rules).
import * as Sentry from "@sentry/react";
import type { RuntimeConfig } from "../runtimeConfig.js";

const SENTRY_DSN =
  "https://04cdd66329cf4f138d7e6edbe37c5785@o1127144.ingest.sentry.io/6168786";

interface InitSentryOptions {
  runtimeConfig?: RuntimeConfig;
  initialScope?: Parameters<typeof Sentry.init>[0]["initialScope"];
}

/**
 * Initialise Sentry. Safe to call before runtime config is loaded — in that
 * case the SDK initialises with `environment: "bootstrap"` so config-load
 * errors can still be captured. After `loadRuntimeConfig()` succeeds, call
 * again with the config to attach the real environment / replay sample
 * rates etc.
 */
export function initSentry({
  runtimeConfig,
  initialScope,
}: InitSentryOptions = {}): void {
  const deployEnv = runtimeConfig?.VITE_DEPLOY_ENV;
  // Bootstrap-stage (deployEnv === undefined): enable only on production
  // builds (Vite-built bundles served from a real env), so config-fetch
  // failures still reach Sentry from stg/partner/prod but local Vite
  // `pnpm dev` runs (where PROD === false) stay silent. Post-config
  // (deployEnv set): defer to the explicit `local` opt-out.
  const enabled =
    deployEnv === undefined ? import.meta.env.PROD : deployEnv !== "local";
  // Add BrowserTracing + Replay only in the post-config init pass.
  // Sentry.Replay is a singleton per page — instantiating it twice (once
  // in the bootstrap-stage init, once in the post-config init) throws
  // "Multiple Sentry Session Replay instances are not supported" and
  // breaks bootstrap. Bootstrap-stage init still captures exceptions
  // (basic SDK functionality, no integrations needed for that).
  const isPostConfig = deployEnv !== undefined;
  const integrations = isPostConfig
    ? [
        new Sentry.BrowserTracing(),
        new Sentry.Replay({
          maskAllText: false,
          maskAllInputs: false,
        }),
      ]
    : [];
  // Release tag tracks the deployed commit SHA from runtime config
  // (`/config.json` `version`, populated by the CodePipeline deploy
  // stage). The bootstrap-stage init has no runtime config yet, so
  // events captured before `loadRuntimeConfig()` resolves carry no
  // release — accepted trade-off for catching config-load failures.
  Sentry.init({
    enabled,
    dsn: SENTRY_DSN,
    integrations,
    environment: deployEnv ?? "bootstrap",
    initialScope,
    release: runtimeConfig?.version,
    beforeSend: function (event, hint) {
      const exception = hint?.originalException;

      if (exception instanceof Error) {
        event.fingerprint = ["{{ default }}", exception.message];
      }

      return event;
    },
    replaysSessionSampleRate:
      deployEnv === "dev" || deployEnv === "stg" ? 1 : 0,
    replaysOnErrorSampleRate: 1.0,
  });
}
